'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetUserPnlChartRow, useActiveWallet, useGetUserPnlChart, usePrefetchGetUserPnlChart } from '@morfi/backend-sdk-v2/client';
import { isI64, isSignedMagnitudeU128 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { GiftButton } from '@/components/gift/gift-button';
import { FundingEntry } from '@/components/onramp/funding-entry';
import { PnlCardLiveline, type PnlLivelinePoint } from '@/components/portfolio/cards/pnl-card-liveline';
import { PnlChartCard } from '@/components/portfolio/cards/pnl-chart-card';
import { PortfolioBalanceTiles, PortfolioCurrentSummary } from '@/components/portfolio/cards/portfolio-current-summary';
import { useRefetchWhenVisible } from '@/components/portfolio/cards/use-refetch-when-visible';
import { useResolvedSelection } from '@/components/portfolio/header/use-resolved-selection';
import { truncateAddress } from '@/components/portfolio/portfolio-helpers';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { DropdownEntry } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowDown03Icon, ArrowUpRight01Icon, ExpandIcon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { PulseDot } from '@/components/ui/pulse-dot';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { UnavailableWalletBadge } from '@/components/ui/unavailable-wallet-badge';
import { D6_SCALE } from '@/config/solana';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { logger } from '@/lib/telemetry/logger';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { usePortfolioWalletSelectionStore } from '@/stores/portfolio-wallet-selection';
import type { Wallet } from '@/types/auth';
import { PnlHeroTimeframes, type PnlHeroWindow } from './pnl-hero-timeframes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PortfolioPnlCardProps {
  wallets: ReadonlyArray<Wallet>;
  registeredAt: string | null;
  viewerMode: 'own' | 'other';
  userId: string;
  /** Query identity owned by the profile portfolio snapshot provider. */
  snapshotUserId: string;
  /**
   * Single wallet pubkey — used to KEY the holdings/pnl REST fetches
   * (single-wallet mode) but NOT for the realtime subscription identity.
   * Realtime threads `walletPubkeys` (array) instead so subscriptions
   * cover EVERY wallet.
   */
  walletPubkey: string;
  /**
   * Every wallet pubkey the user owns. Threaded as `walletPubkeys:`
   * (array) into per-user-feed hooks so realtime subscriptions cover
   * EVERY wallet (a single-wallet subscription silently misses the rest).
   */
  walletPubkeys: readonly string[];
}
type PortfolioPerformance = NonNullable<GetUserPnlChartRow['portfolioPerformance']>;
type PnlLinePoint = PortfolioPerformance['line'][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map the portfolio-performance history to the sparkline's point shape. Its
 * open leg is the server's five-minute estimate, so the line and the delta
 * below it move together but neither reconciles to the last cent with the
 * Total above, which marks live against pool depth. Number conversion below is
 * only for the display-only sparkline after signed-I64 validation.
 */
function adaptPerformanceSeries(line: ReadonlyArray<PnlLinePoint> | undefined): ReadonlyArray<PnlLivelinePoint> | null {
  if (line === undefined || line.length < 2) return null;
  const points = line.map((point): PnlLivelinePoint | null => {
    const valueD6 = signedI64Wire(point.value);
    if (valueD6 === null) return null;
    return {
      time: new Date(point.timestamp).getTime() / 1000,
      value: Number(valueD6) / D6_SCALE
    };
  });
  return points.every((point): point is PnlLivelinePoint => point !== null) ? points : null;
}
function signedI64Wire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|-?[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}
function signedAggregateD6Wire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|-?[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isSignedMagnitudeU128(parsed) ? parsed : null;
}

// ---------------------------------------------------------------------------
// Internal subcomponents
// ---------------------------------------------------------------------------

interface PnlCardActionsProps {
  fundingEnabled: boolean;
  onDeposit: () => void;
  onWithdraw: () => void;
  onGiftClickBefore: () => void;
}
function PnlCardActions(t0) {
  const $ = _c(10);
  const {
    fundingEnabled,
    onDeposit,
    onWithdraw,
    onGiftClickBefore
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  let t1;
  if ($[0] !== fundingEnabled || $[1] !== onDeposit || $[2] !== onWithdraw || $[3] !== t) {
    t1 = fundingEnabled ? <><AnimatedButton variant="primary" size="xl" className="grow basis-full sm:basis-0" onClick={onDeposit} aria-label={t("deposit")}><Icon icon={ArrowDown03Icon} className="size-5" aria-hidden="true" /><span>{t("deposit")}</span></AnimatedButton><AnimatedButton variant="secondary" size="xl" className="grow basis-0" onClick={onWithdraw} aria-label={t("withdraw")}><Icon icon={ArrowUpRight01Icon} className="size-5" aria-hidden="true" /><span>{t("withdraw")}</span></AnimatedButton></> : null;
    $[0] = fundingEnabled;
    $[1] = onDeposit;
    $[2] = onWithdraw;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== onGiftClickBefore) {
    t2 = <GiftButton variant="secondary" size="xl" className="grow basis-0 sm:grow-0 sm:basis-auto" onClickBefore={onGiftClickBefore} />;
    $[5] = onGiftClickBefore;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="flex flex-wrap gap-2">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single-card portfolio hero. Renders Total + selected-timeframe PnL change +
 * Cash/Holdings tiles + Deposit/Withdraw/Gift buttons.
 */
export function PortfolioPnlCard(t0) {
  const $ = _c(147);
  const {
    wallets,
    registeredAt,
    viewerMode,
    userId,
    snapshotUserId,
    walletPubkeys
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  const tStats = useTranslations("profile.stats");
  const tError = useTranslations("errorBoundary");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      range: "1d"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [window, setWindow] = useState(t1);
  const prefetchPnlChart = usePrefetchGetUserPnlChart();
  const tPicker = useTranslations("profile.next.picker");
  const tWallet = useTranslations("wallet.table");
  const {
    setActiveWalletId
  } = useActiveWallet();
  const {
    enabled: multiWalletEnabled
  } = useMultiWalletFeature();
  const onrampEnabled = useFeatureGate("onramp_enabled") === true;
  const setSelection = usePortfolioWalletSelectionStore(_temp);
  let t2;
  if ($[1] !== wallets) {
    t2 = [...wallets];
    $[1] = wallets;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const resolved = useResolvedSelection(t2);
  let pickerTriggerLabel;
  let t3;
  if ($[3] !== resolved.mode || $[4] !== resolved.wallet || $[5] !== setSelection || $[6] !== t || $[7] !== tPicker || $[8] !== tWallet || $[9] !== wallets) {
    const activeWallets = wallets.filter(_temp2);
    const showWalletAddresses = activeWallets.length > 1;
    let t4;
    if ($[12] !== resolved.mode || $[13] !== resolved.wallet || $[14] !== t) {
      t4 = resolved.mode === "single" && resolved.wallet !== null ? resolved.wallet.label : t("total_label");
      $[12] = resolved.mode;
      $[13] = resolved.wallet;
      $[14] = t;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    pickerTriggerLabel = t4;
    const t5 = resolved.mode === "all" ? "success" : "muted";
    let t6;
    if ($[16] !== t5) {
      t6 = <PulseDot size="sm" color={t5} static={true} />;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== tPicker) {
      t7 = tPicker("all_wallets");
      $[18] = tPicker;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t7) {
      t8 = <span className="truncate">{t7}</span>;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t6 || $[23] !== t8) {
      t9 = <span className="flex items-center gap-2">{t6}{t8}</span>;
      $[22] = t6;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== setSelection) {
      t10 = () => {
        setSelection("all");
      };
      $[25] = setSelection;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t10 || $[28] !== t9) {
      t11 = {
        label: t9,
        onSelect: t10
      };
      $[27] = t10;
      $[28] = t9;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== resolved.mode || $[31] !== resolved.wallet?.id || $[32] !== setSelection || $[33] !== showWalletAddresses || $[34] !== tWallet) {
      t12 = w_0 => {
        const isSelected = resolved.mode === "single" && resolved.wallet?.id === w_0.id;
        return {
          label: <span className="flex items-center gap-2"><PulseDot size="sm" color={isSelected ? "success" : "muted"} static={true} /><span className="truncate">{w_0.label}</span>{w_0.isAvailable === false ? <UnavailableWalletBadge label={tWallet("unavailable_on_device")} /> : null}{showWalletAddresses ? <span className="shrink-0 text-caption text-foreground-secondary">{truncateAddress(w_0.address)}</span> : null}</span>,
          onSelect: () => {
            setSelection(w_0.id);
          },
          disabled: w_0.isAvailable === false
        };
      };
      $[30] = resolved.mode;
      $[31] = resolved.wallet?.id;
      $[32] = setSelection;
      $[33] = showWalletAddresses;
      $[34] = tWallet;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    t3 = [t11, ...activeWallets.map(t12)];
    $[3] = resolved.mode;
    $[4] = resolved.wallet;
    $[5] = setSelection;
    $[6] = t;
    $[7] = tPicker;
    $[8] = tWallet;
    $[9] = wallets;
    $[10] = pickerTriggerLabel;
    $[11] = t3;
  } else {
    pickerTriggerLabel = $[10];
    t3 = $[11];
  }
  const pickerItems = t3;
  const queryPubkey = resolved.mode === "single" && resolved.wallet !== null ? resolved.wallet.address : userId;
  let t4;
  if ($[36] !== resolved.mode || $[37] !== resolved.wallet || $[38] !== walletPubkeys) {
    t4 = resolved.mode === "single" && resolved.wallet !== null ? [resolved.wallet.address] : walletPubkeys;
    $[36] = resolved.mode;
    $[37] = resolved.wallet;
    $[38] = walletPubkeys;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  const realtimeWalletPubkeys = t4;
  let t5;
  if ($[40] !== queryPubkey || $[41] !== realtimeWalletPubkeys || $[42] !== window.range) {
    t5 = {
      pubkey: queryPubkey,
      walletPubkeys: realtimeWalletPubkeys,
      range: window.range
    };
    $[40] = queryPubkey;
    $[41] = realtimeWalletPubkeys;
    $[42] = window.range;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      refetchInterval: false
    };
    $[44] = t6;
  } else {
    t6 = $[44];
  }
  const pnlResult = useGetUserPnlChart(t5, t6);
  useRefetchWhenVisible(pnlResult);
  const [previous, setPrevious] = useState(null);
  let t7;
  if ($[45] !== realtimeWalletPubkeys) {
    t7 = realtimeWalletPubkeys.join(",");
    $[45] = realtimeWalletPubkeys;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  const identity = `${queryPubkey}:${t7}`;
  let t8;
  let t9;
  if ($[47] !== identity || $[48] !== pnlResult.data || $[49] !== pnlResult.isPlaceholderData || $[50] !== pnlResult.isSuccess || $[51] !== window) {
    t8 = () => {
      if (pnlResult.isSuccess && !pnlResult.isPlaceholderData && pnlResult.data !== undefined) {
        setPrevious({
          identity,
          window,
          data: pnlResult.data
        });
      }
    };
    t9 = [identity, window, pnlResult.isSuccess, pnlResult.isPlaceholderData, pnlResult.data];
    $[47] = identity;
    $[48] = pnlResult.data;
    $[49] = pnlResult.isPlaceholderData;
    $[50] = pnlResult.isSuccess;
    $[51] = window;
    $[52] = t8;
    $[53] = t9;
  } else {
    t8 = $[52];
    t9 = $[53];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[54] !== pnlResult.error || $[55] !== window.range) {
    t10 = () => {
      if (pnlResult.error) {
        logger.warn("portfolio.pnl.hero_load_failed", {
          range: window.range,
          errorName: pnlResult.error.name
        });
      }
    };
    t11 = [pnlResult.error, window.range];
    $[54] = pnlResult.error;
    $[55] = window.range;
    $[56] = t10;
    $[57] = t11;
  } else {
    t10 = $[56];
    t11 = $[57];
  }
  useEffect(t10, t11);
  const isTransitioning = pnlResult.isPending || pnlResult.isPlaceholderData;
  const retained = isTransitioning && previous?.identity === identity ? previous : null;
  const pnlData = pnlResult.isError ? undefined : isTransitioning ? retained?.data : pnlResult.data;
  const renderWindow = retained?.window ?? window;
  let t12;
  if ($[58] !== registeredAt || $[59] !== renderWindow.range) {
    t12 = renderWindow.range === "all" && registeredAt !== null ? {
      startTime: Date.parse(registeredAt) / 1000
    } : {};
    $[58] = registeredAt;
    $[59] = renderWindow.range;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  const viewport = t12;
  const performance = pnlData?.portfolioPerformance ?? undefined;
  const t13 = performance?.line;
  let t14;
  if ($[61] !== t13) {
    t14 = adaptPerformanceSeries(t13);
    $[61] = t13;
    $[62] = t14;
  } else {
    t14 = $[62];
  }
  const series = t14;
  const t15 = performance?.deltaD6;
  let t16;
  if ($[63] !== t15) {
    t16 = signedAggregateD6Wire(t15);
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  const deltaD6 = t16;
  const [qrOpen, setQrOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pnlOpen, setPnlOpen] = useState(false);
  let t17;
  if ($[65] !== resolved.mode || $[66] !== resolved.wallet || $[67] !== wallets) {
    t17 = function targetWallet() {
      if (resolved.mode === "single") {
        return resolved.wallet ?? undefined;
      }
      return wallets.find(_temp3);
    };
    $[65] = resolved.mode;
    $[66] = resolved.wallet;
    $[67] = wallets;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  const targetWallet = t17;
  let t18;
  if ($[69] !== setActiveWalletId || $[70] !== targetWallet) {
    t18 = function handleDeposit() {
      const target = targetWallet();
      if (target !== undefined) {
        setActiveWalletId(target.id);
      }
      setQrOpen(true);
    };
    $[69] = setActiveWalletId;
    $[70] = targetWallet;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  const handleDeposit = t18;
  let t19;
  if ($[72] !== setActiveWalletId || $[73] !== targetWallet) {
    t19 = function handleWithdraw() {
      const target_0 = targetWallet();
      if (target_0 !== undefined) {
        setActiveWalletId(target_0.id);
      }
      setTransferOpen(true);
    };
    $[72] = setActiveWalletId;
    $[73] = targetWallet;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  const handleWithdraw = t19;
  let t20;
  if ($[75] !== setActiveWalletId || $[76] !== targetWallet) {
    t20 = function handleGiftClickBefore() {
      const target_1 = targetWallet();
      if (target_1 !== undefined) {
        setActiveWalletId(target_1.id);
      }
    };
    $[75] = setActiveWalletId;
    $[76] = targetWallet;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  const handleGiftClickBefore = t20;
  let t21;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setPnlOpen(true);
    $[78] = t21;
  } else {
    t21 = $[78];
  }
  let t22;
  if ($[79] !== t) {
    t22 = t("expand_pnl");
    $[79] = t;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon icon={ExpandIcon} className="size-3.5" aria-hidden="true" />;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  let t24;
  if ($[82] !== pnlOpen || $[83] !== t22) {
    t24 = <AnimatedButton variant="tertiary" size="xs" onClick={t21} aria-label={t22} aria-haspopup="dialog" aria-expanded={pnlOpen} className="absolute right-3 top-3 z-10" data-testid="portfolio-pnl-card-expand">{t23}</AnimatedButton>;
    $[82] = pnlOpen;
    $[83] = t22;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  const t25 = series === null ? null : deltaD6;
  let t26;
  if ($[85] !== multiWalletEnabled || $[86] !== pickerItems || $[87] !== pickerTriggerLabel || $[88] !== realtimeWalletPubkeys || $[89] !== snapshotUserId || $[90] !== t25 || $[91] !== viewerMode) {
    t26 = <div className="shrink-0 px-4"><PortfolioCurrentSummary userId={snapshotUserId} walletPubkeys={realtimeWalletPubkeys} viewerMode={viewerMode} pickerTriggerLabel={pickerTriggerLabel} pickerItems={pickerItems} showWalletPicker={multiWalletEnabled} deltaD6={t25} /></div>;
    $[85] = multiWalletEnabled;
    $[86] = pickerItems;
    $[87] = pickerTriggerLabel;
    $[88] = realtimeWalletPubkeys;
    $[89] = snapshotUserId;
    $[90] = t25;
    $[91] = viewerMode;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== deltaD6 || $[94] !== isTransitioning || $[95] !== pnlResult || $[96] !== series || $[97] !== tError || $[98] !== viewport) {
    t27 = pnlResult.isError ? <div className="flex h-full flex-col items-center justify-center gap-3 px-4" role="alert"><span className="text-body text-foreground-secondary">{tError("title")}</span><AnimatedButton variant="tertiary" size="sm" onClick={() => {
        pnlResult.refetch({
          cancelRefetch: false
        });
      }}>{tError("retry")}</AnimatedButton></div> : isTransitioning && series === null ? <Skeleton className="absolute inset-0" data-testid="portfolio-pnl-chart-loading" /> : series !== null ? <div className="absolute inset-0"><PnlCardLiveline series={series} {...viewport} tone={(deltaD6 ?? BigInt(0)) >= BigInt(0) ? "success" : "destructive"} /></div> : null;
    $[93] = deltaD6;
    $[94] = isTransitioning;
    $[95] = pnlResult;
    $[96] = series;
    $[97] = tError;
    $[98] = viewport;
    $[99] = t27;
  } else {
    t27 = $[99];
  }
  let t28;
  if ($[100] !== isTransitioning || $[101] !== t27) {
    t28 = <div className="relative h-32 w-full min-w-0 shrink-0 overflow-hidden" data-testid="portfolio-pnl-card-chart-wrapper" aria-busy={isTransitioning}>{t27}</div>;
    $[100] = isTransitioning;
    $[101] = t27;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== prefetchPnlChart || $[104] !== queryPubkey || $[105] !== realtimeWalletPubkeys) {
    t29 = next => {
      prefetchPnlChart({
        pubkey: queryPubkey,
        walletPubkeys: realtimeWalletPubkeys,
        range: next.range
      });
    };
    $[103] = prefetchPnlChart;
    $[104] = queryPubkey;
    $[105] = realtimeWalletPubkeys;
    $[106] = t29;
  } else {
    t29 = $[106];
  }
  let t30;
  if ($[107] !== identity || $[108] !== t29 || $[109] !== window) {
    t30 = <div className="shrink-0 px-4"><PnlHeroTimeframes key={identity} window={window} onChange={setWindow} onPrefetch={t29} /></div>;
    $[107] = identity;
    $[108] = t29;
    $[109] = window;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] !== realtimeWalletPubkeys || $[112] !== snapshotUserId) {
    t31 = <div className="shrink-0 px-4"><PortfolioBalanceTiles userId={snapshotUserId} walletPubkeys={realtimeWalletPubkeys} /></div>;
    $[111] = realtimeWalletPubkeys;
    $[112] = snapshotUserId;
    $[113] = t31;
  } else {
    t31 = $[113];
  }
  let t32;
  if ($[114] !== handleDeposit || $[115] !== handleGiftClickBefore || $[116] !== handleWithdraw || $[117] !== onrampEnabled || $[118] !== viewerMode) {
    t32 = viewerMode === "own" ? <div className="shrink-0 px-4"><PnlCardActions fundingEnabled={onrampEnabled} onDeposit={handleDeposit} onWithdraw={handleWithdraw} onGiftClickBefore={handleGiftClickBefore} /></div> : null;
    $[114] = handleDeposit;
    $[115] = handleGiftClickBefore;
    $[116] = handleWithdraw;
    $[117] = onrampEnabled;
    $[118] = viewerMode;
    $[119] = t32;
  } else {
    t32 = $[119];
  }
  let t33;
  if ($[120] !== t26 || $[121] !== t28 || $[122] !== t30 || $[123] !== t31 || $[124] !== t32) {
    t33 = <div className="flex min-h-96 min-w-0 flex-1 flex-col gap-3 py-4" data-testid="portfolio-pnl-card-hero-grid">{t26}{t28}{t30}{t31}{t32}</div>;
    $[120] = t26;
    $[121] = t28;
    $[122] = t30;
    $[123] = t31;
    $[124] = t32;
    $[125] = t33;
  } else {
    t33 = $[125];
  }
  let t34;
  if ($[126] !== t24 || $[127] !== t33) {
    t34 = <Panel density="flush" className="relative h-full">{t24}{t33}</Panel>;
    $[126] = t24;
    $[127] = t33;
    $[128] = t34;
  } else {
    t34 = $[128];
  }
  let t35;
  if ($[129] !== onrampEnabled || $[130] !== qrOpen || $[131] !== transferOpen || $[132] !== viewerMode) {
    t35 = viewerMode === "own" && onrampEnabled ? <><FundingEntry prewarm={true} open={qrOpen} onClose={() => setQrOpen(false)} /><FundingEntry mode="withdraw" prewarm={true} open={transferOpen} onClose={() => setTransferOpen(false)} /></> : null;
    $[129] = onrampEnabled;
    $[130] = qrOpen;
    $[131] = transferOpen;
    $[132] = viewerMode;
    $[133] = t35;
  } else {
    t35 = $[133];
  }
  let t36;
  if ($[134] !== tStats) {
    t36 = tStats("pnl_chart_title");
    $[134] = tStats;
    $[135] = t36;
  } else {
    t36 = $[135];
  }
  let t37;
  if ($[136] !== queryPubkey || $[137] !== registeredAt) {
    t37 = <PnlChartCard pubkey={queryPubkey} registeredAt={registeredAt} hideTitle={true} />;
    $[136] = queryPubkey;
    $[137] = registeredAt;
    $[138] = t37;
  } else {
    t37 = $[138];
  }
  let t38;
  if ($[139] !== pnlOpen || $[140] !== t36 || $[141] !== t37) {
    t38 = <SilkSheet open={pnlOpen} onOpenChange={setPnlOpen} variant="detached" size="xl" title={t36}>{t37}</SilkSheet>;
    $[139] = pnlOpen;
    $[140] = t36;
    $[141] = t37;
    $[142] = t38;
  } else {
    t38 = $[142];
  }
  let t39;
  if ($[143] !== t34 || $[144] !== t35 || $[145] !== t38) {
    t39 = <>{t34}{t35}{t38}</>;
    $[143] = t34;
    $[144] = t35;
    $[145] = t38;
    $[146] = t39;
  } else {
    t39 = $[146];
  }
  return t39;
}
function _temp3(w_1) {
  return w_1.archivedAt === null;
}
function _temp2(w) {
  return w.archivedAt === null;
}
function _temp(s) {
  return s.setSelection;
}